import { Button } from '@neo/primitives/button'
import { Input } from '@neo/primitives/input'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@neo/primitives/sheet'
import { Skeleton } from '@neo/primitives/skeleton'
import { Typography } from '@neo/primitives/typography'
import { ArrowLeft, Search } from 'lucide-react'
import { useState } from 'react'
import { useExternalAppsV2Query } from 'v2/pages/settings/integrations/queries'
import type { RExternalApp, RExternalAppDetails } from 'v2/types/api/settings'
import { ACI_RETURN_TO_KEY } from '../oauth-redirect'
import { AddMcpButton } from './add-mcp-button'
import { CategoryFilter, type CategoryOption } from './category-filter'
import { CustomMcpForm } from './custom-mcp-form'
import { IntegrationAppSelection } from './integration-app-selection'

interface IntegrationSideSheetProps {
  onClose: () => void
}

interface CatalogBodyProps {
  externalApps: RExternalApp | undefined
  isLoading: boolean
  error: unknown
  searchTerm: string
  activeCategories: string[]
  onAppClick: (app: RExternalAppDetails) => void
}

/**
 * The catalog half of the sheet, with its three states made explicit.
 *
 * Previously this rendered `externalApps && <IntegrationAppSelection …>`, so both
 * "still loading" and "request failed" collapsed into an empty sheet under a live
 * search box — indistinguishable from "your org has no apps", and permanent on
 * failure.
 */
const CatalogBody = ({
  externalApps,
  isLoading,
  error,
  searchTerm,
  activeCategories,
  onAppClick,
}: CatalogBodyProps) => {
  if (isLoading)
    return (
      <div className="mt-4 space-y-2" data-testid="loader-integration-catalog">
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton key={`integration-catalog-skeleton-${index}`} className="h-10 w-full" />
        ))}
      </div>
    )

  if (error || !externalApps)
    return (
      <div
        className="mt-6 flex flex-col items-center gap-2 text-center"
        data-testid="error-integration-catalog"
      >
        <Typography variant="P/500">Could not load the app catalog.</Typography>
        <Typography variant="XS/400" className="text-t3">
          Close this panel and try again.
        </Typography>
      </div>
    )

  return (
    <IntegrationAppSelection
      externalApps={externalApps}
      searchTerm={searchTerm}
      activeCategories={activeCategories}
      onAppClick={onAppClick}
    />
  )
}

export const openConnectPage = (app: Pick<RExternalAppDetails, 'provider' | 'id'>) => {
  // The OAuth return lands in the new tab, so it must carry where the connect started: a pending
  // address (the KB "Connect now" banner) if any, else this page. The pending key is cleared here,
  // since this tab's sessionStorage is not shared and a stale key would hijack a later return.
  let returnTo = window.location.pathname + window.location.search
  try {
    returnTo = sessionStorage.getItem(ACI_RETURN_TO_KEY) || returnTo
    sessionStorage.removeItem(ACI_RETURN_TO_KEY)
  } catch {
    // Storage unavailable (private mode): return to this page.
  }
  window.open(
    `/integrations/connect/${encodeURIComponent(app.provider || app.id)}?return_to=${encodeURIComponent(returnTo)}`,
    '_blank',
    'noopener,noreferrer',
  )
}

export const IntegrationSideSheet = ({ onClose }: IntegrationSideSheetProps) => {
  const {
    data: externalApps,
    isLoading: isCatalogLoading,
    error: catalogError,
  } = useExternalAppsV2Query()
  const [searchTerm, setSearchTerm] = useState('')
  const [activeCategories, setActiveCategories] = useState<string[]>([])
  const [customMode, setCustomMode] = useState<'mcp' | null>(null)

  const isCustomMcp = customMode === 'mcp'
  // An empty category gets no option: it would filter to nothing.
  const catalogCategories: CategoryOption[] = Object.entries(externalApps ?? {}).flatMap(
    ([name, apps]) => ((apps?.length ?? 0) > 0 ? [{ name, count: apps?.length ?? 0 }] : []),
  )
  return (
    <Sheet open onOpenChange={onClose}>
      {/* `detached` matches every other console side panel (`SideSheet`'s default). */}
      <SheetContent
        detached
        className="flex w-[65%] flex-col sm:w-[65%] md:w-[65%] lg:max-w-[550px]"
        // Skip Radix autofocus on the search box: this panel is for browsing. Focus stays trapped.
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <SheetHeader className="flex-row items-center gap-1 space-y-0">
          {isCustomMcp && (
            <Button
              variant="ghost"
              tooltipContent="Back to integrations"
              onClick={() => setCustomMode(null)}
              className="-ml-1 flex-none pl-0 hover:bg-transparent"
              data-testid="button-back-to-app-list"
            >
              <ArrowLeft className="h-6 w-6 text-secondary" />
            </Button>
          )}
          {/* `text-h3-600` matches every other sheet header in the console. */}
          <SheetTitle className="-ml-2 min-w-0 text-h3-600 first:ml-0">
            {isCustomMcp ? 'Connect an MCP server' : 'Add tools'}
          </SheetTitle>
        </SheetHeader>

        {!isCustomMcp && (
          // No padding: the sheet's p-6 is the content box the rows span, and `SheetContent`'s
          // `gap-4` already spaces it from the header.
          <div className="flex items-center gap-2">
            <Input
              placeholder="Search apps and tools"
              className="flex-1"
              startIcon={Search}
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              data-testid="input-search-apps"
            />
            <AddMcpButton onClick={() => setCustomMode('mcp')} />
            <CategoryFilter
              categories={catalogCategories}
              value={activeCategories}
              onChange={setActiveCategories}
            />
          </div>
        )}

        {isCustomMcp ? (
          <CustomMcpForm onConnected={onClose} />
        ) : (
          <div className="flex-grow overflow-hidden">
            <CatalogBody
              externalApps={externalApps}
              isLoading={isCatalogLoading}
              error={catalogError}
              searchTerm={searchTerm}
              activeCategories={activeCategories}
              onAppClick={openConnectPage}
            />
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
